import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api, {
  STORAGE,
  guardarSesion,
  leerSesion,
  borrarSesion,
  recordarCodigo,
  mensajeDeError,
} from '../lib/api';

/**
 * Sesión del paciente y del profesional.
 *
 * El paciente no tiene cuenta: tiene un código de continuidad. La primera vez
 * se le entrega uno y con él vuelve. Aquí no se guarda ningún dato personal.
 */

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const sesion = leerSesion();
    if (sesion) {
      setToken(sesion.token);
      setUser({
        pseudonym: sesion.pseudonym,
        patient_id: sesion.patient_id,
        name: sesion.name,
        type: sesion.type,
      });
    }
  }, []);

  /**
   * Crea una identidad anónima nueva.
   * Devuelve el código de continuidad, que se muestra UNA sola vez.
   */
  const crearIdentidad = useCallback(async (pseudonym) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.post('/auth/anonymous', { pseudonym });

      guardarSesion(data);
      recordarCodigo(data.continuity_code);

      setToken(data.access_token);
      setUser({ pseudonym: data.pseudonym, patient_id: data.patient_id, type: 'patient' });

      return { ok: true, codigo: data.continuity_code, pseudonym: data.pseudonym };
    } catch (err) {
      const mensaje = mensajeDeError(err, 'No pudimos crear tu sesión anónima');
      setError(mensaje);
      return { ok: false, error: mensaje };
    } finally {
      setLoading(false);
    }
  }, []);

  /** Recupera una identidad existente con el código de continuidad. */
  const recuperarIdentidad = useCallback(async (code) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.post('/auth/patient/recover', { code });

      guardarSesion(data);
      recordarCodigo(code.trim().toUpperCase());

      setToken(data.access_token);
      setUser({ pseudonym: data.pseudonym, patient_id: data.patient_id, type: 'patient' });

      return { ok: true, data };
    } catch (err) {
      const mensaje = mensajeDeError(err, 'No pudimos recuperar tu sesión');
      setError(mensaje);
      return { ok: false, error: mensaje };
    } finally {
      setLoading(false);
    }
  }, []);

  /** Acceso del profesional. */
  const loginProfesional = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const { data } = await api.post('/auth/psychologist/login', { email, password });

      sessionStorage.setItem(STORAGE.token, data.access_token);
      sessionStorage.setItem(
        STORAGE.user,
        JSON.stringify({ name: data.name, type: 'psychologist', session_id: data.session_id })
      );

      setToken(data.access_token);
      setUser({ name: data.name, type: 'psychologist' });

      return { ok: true, data, success: true };
    } catch (err) {
      const mensaje = mensajeDeError(err, 'Credenciales inválidas');
      setError(mensaje);
      return { ok: false, success: false, error: mensaje };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    borrarSesion();
    setUser(null);
    setToken(null);
    setError(null);
  }, []);

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: Boolean(token),
    isPatient: user?.type === 'patient',
    isPsychologist: user?.type === 'psychologist',
    crearIdentidad,
    recuperarIdentidad,
    loginProfesional,
    logout,

    // Nombres anteriores, para no romper pantallas que aún los usan.
    authenticateAnonymous: crearIdentidad,
    loginPsychologist: loginProfesional,
    getAuthHeaders: () => (token ? { Authorization: `Bearer ${token}` } : {}),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export default AuthContext;
